import Vue from "vue";
import Vuex from "vuex";
Vue.use(Vuex);
import VueCookies from "vue-cookies";
Vue.use(VueCookies);

import axios from "axios";
import LocalStorageService from "./LocalStorageService";
import webAppConfig from "../commons/webApp.config";
import {commonStore } from "./commonStore";
import appLogo from "../../assets/images/logo/inistate-logo.png";

const requestConfig = {
  baseURL: webAppConfig.baseURL,
};

function getClient() {
  let client = axios.create({
    ...requestConfig,
    headers: {
      "Content-Type": "application/json",
      "Authorization": "Bearer " + LocalStorageService.getAccessToken(),
    },
  });
  // Interceptors
  client.interceptors.response.use((response) => {
    return response;
  }, function (error) {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
        let localStorage = LocalStorageService;
        originalRequest._retry = true;
        const config = {
          ...requestConfig,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        };
        const data = `grant_type=refresh_token&refresh_token=${localStorage.getRefreshToken()}`;
        return axios.post('/token', data, config)
          .then(res => {
            if (res.status === 200) {
              localStorage.setToken(res.data);
              client.defaults.headers['Authorization'] = 'Bearer ' + localStorage.getAccessToken();
              originalRequest.headers['Authorization'] = 'Bearer ' + localStorage.getAccessToken();
              return axios(originalRequest);
            }
          })
          .catch(err => {
            console.error(['Authorization:Interceptor'], err);
            authorizationStore.dispatch('logout');
          });
    } else if (error.response && error.response.status === 401) {
      authorizationStore.dispatch('logout');
    } else {
      console.error(['Authorization:Interceptor'], error);
    }
    return Promise.reject(error);
  });
  return client;
}

export const authorizationStore = new Vuex.Store({
  state: {
    client: getClient(),
    userName: LocalStorageService.getUserName(),
    roles: LocalStorageService.getRoles(),
    userProfile: {},
    appConfig: null,
  },
  mutations: {
    setUserName(state, payload) {
      state.userName = payload;
    },
    setRoles(state, data) {
      state.roles = data;
    },
    setUserProfile(state, data) {
      state.userProfile = {
        ...data,
        userName: state.userName || LocalStorageService.getUserName(),
      }
    },
    setAppConfig(state, data) {
      state.appConfig = data;
    },
  },
  actions: {
    async login({ state }, payload) {
      try {
        const res = await axios.post(
          '/token',
          'grant_type=password&username=' + payload.username + '&password=' + encodeURIComponent(payload.password),
          {
            ...requestConfig,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
          }
        );
        if (res.data) {
          const authorizationData = res.data;
          localStorage.setItem('ls.authorizationData', JSON.stringify({
            access_token: authorizationData.access_token,
            refresh_token: authorizationData.refresh_token,
            userName: authorizationData.userName,
          }));

          var commonClient = commonStore.state.client;
          commonClient.defaults.headers['Authorization'] = "Bearer " + authorizationData.access_token;
          state.client = getClient();

        } else {
          throw 'error';
        }
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    async joinInformation({ state }, payload) {
      return axios.post('/api/workspace/join', payload, requestConfig);
    },
    async register({ state }, payload) {
      return await axios.post('/api/workspace/register', payload, requestConfig);
    },
    async forgotPassword({ state }, payload) {
      return await axios.post('/api/user/ForgotPassword', payload, requestConfig);
    },
    async resetPassword({ state }, payload) {
      payload.newPassword = payload.password;
      return await axios.post('/api/user/ForgotPassword', payload, requestConfig);
    },
    async externalLogin({ state }, payload) {
      let result = await axios.post('/api/user/externalLogin', payload, requestConfig);
      if (result && result.data && result.data.authCode) {
        try {
          const res = await axios.post(
            '/token',
            `grant_type=refresh_token&refresh_token=${result.data.authCode}`, 
            {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
            }
          );
          if (res.data) {
            const authorizationData = res.data
            localStorage.setItem('ls.authorizationData', JSON.stringify({
              access_token: authorizationData.access_token,
              refresh_token: authorizationData.refresh_token,
              userName: authorizationData.userName,
            }));

            var commonClient = commonStore.state.client;
            commonClient.defaults.headers['Authorization'] = "Bearer " + authorizationData.access_token;
            state.client = getClient();

            return true;
          } else {
            throw 'error';
          }
        } catch (err) {
          console.error(err);
          throw err;
        }
      }

      return result;
    },
    async activate({ state }, payload) {
      let result = await axios.post('/api/workspace/activate', payload, requestConfig);
      if (result && result.data && result.data.authCode) {
        try {
          const res = await axios.post(
            '/token',
            `grant_type=refresh_token&refresh_token=${result.data.authCode}`,
            {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
            }
          );
          if (res.data) {
            const authorizationData = res.data
            localStorage.setItem('ls.authorizationData', JSON.stringify({
              access_token: authorizationData.access_token,
              refresh_token: authorizationData.refresh_token,
              /* username does not return */
              // userName: authorizationData.userName,
            }));

            var commonClient = commonStore.state.client;
            commonClient.defaults.headers['Authorization'] = "Bearer " + authorizationData.access_token;
            state.client = getClient();

            return true;
          } else {
            throw 'error';
          }
        } catch (err) {
          console.error(err);
          throw err;
        }
      }

      return false;
    },
    async profile({ state, dispatch, commit }) {
      try {
        const res = await state.client.get('/api/profile');
        if (res.data && res.data.userName) {
          /* set userName */
          commit("setUserName", res.data.userName);
          /* set roles */
          commit("setRoles", res.data.roles);
          /* set user profile */
          let userProfile = res.data;
          delete userProfile.roles;
          commit("setUserProfile", userProfile);
          /* set local storage */
          const lsAuthData = JSON.parse(localStorage.getItem("ls.authorizationData"));
          localStorage.setItem('ls.authorizationData', JSON.stringify({
            ...lsAuthData,
            userName: res.data.userName,
          }));
        } else {
          throw "Profile not available or not found.";
        }
      } catch(err) {
        console.error("[Authorization]", err);
        dispatch('logout');
      }
    },
    setRoles({ commit }, payload) {
      commit('setRoles', payload);
    },
    setUserProfile({ commit }, payload) {
      commit('setUserProfile', payload);
    },
    async updateUserProfile({ state, commit }, payload) {
      try {
        await state.client.post('/api/user/update', payload);
        commit('setUserProfile', payload);
      } catch(err) {
        throw err;
      }
    },
    async changePassword({ state }, payload) {
      return await state.client.post('/api/user/changePassword', payload);
    },
    async forgotPassword({}, payload) {
      /**
       * case a: for sending forget password email
       * case b: for new password
       * e.g.  : payload => email(a,b), code(b), newPassword(b)
       */
      return await axios.post('/api/user/forgotPassword', payload, requestConfig);
    },
    // async getWorkspaceTheme({ }, payload) {
    //   return await axios.get(`/api/workspacetheme/${payload}`, requestConfig);
    // },
    clearToken() {
      LocalStorageService.clearToken();
    },
    logout({ state, dispatch }) {
      /* reset */
      const loggedSource = $cookies.get('loggedSource');
      dispatch('clearToken');
      state.userName = null;
      state.roles = null;
      state.userProfile = {}; 
      /* redirect */
      location.href = "./#/login";
    },
    async getAPIKey({ state }) {
      return await state.client.get('/api/user/generateAPIKey');
    },
    setAppConfig({ commit }, payload) {
      commit('setAppConfig', payload);
    },
  },
  getters: {
    userProfile: state => state.userProfile || {},
    userRoles: state => {
      const dict = [
        { value: 'Administrator', order: 0 },
        { value: 'Consultant', order: 1 },
        { value: 'Workspace Admin', order: 2 },
        { value: 'User Admin', order: 3 },
      ];
      if (Array.isArray(state.roles) && state.roles.length) {
        return state.roles.map(x => {
          const role = dict.find(y => y.value === x);
          return role ? role : { value: x, order: 99 }
        })
          .sort((key, keyB) => key.order - keyB.order)
          .map(x => x.value)
          .join(", ");
      }
      return "Member";
    },
    isAdministrator: (state, getters) => 
      getters.userRoles.indexOf("Administrator") > -1,
    isAdminUser: (state, getters) => 
      getters.userRoles.indexOf("Administrator") > -1 ||
      getters.userRoles.indexOf("Workspace Admin") > -1 ||
      getters.userRoles.indexOf("Consultant") > -1,
    isManagerUser: (state, getters) =>
      getters.userRoles.indexOf("User Admin") > -1,
    isBeta: (state, getters) => 
      getters.userRoles.indexOf("Consultant") > -1 ||
      getters.userRoles.indexOf("Administrator") > -1,
    isInternal: (state, getters) => 
      state.userName && state.userName.slice(-17) === '@gneysoftware.com' ||
      ['admin', 'szewah'].indexOf(state.userName) > -1,
    isUIDesigner: (state) => 
      ['jiaxiang.tan@gneysoftware.com','admin'].indexOf(state.userName) > -1,
    appName: state => state.appConfig && state.appConfig.name || 'Inistate',
    appLogo: state => state.appConfig && state.appConfig.logo || appLogo,
    appDisableThirdPartyLogin: state => state.appConfig && state.appConfig.disableThirdPartyLogin,
  }
})